import { IChangeVisibility } from "src/Pages/Company/OrganizationStructure/ConfigureStructure/Tabs/StructureSettings/StructureSettings.schema";
import { IFormValues } from "src/Pages/Company/OrganizationStructure/Forms/EditStructureForm/EditStructureForm.schema";
import { ICreateStructure } from "src/Pages/Company/OrganizationStructure/ConfigureStructure/CreateStructure/CreateStructure.scheme";
import {
  OrganizationStructureApiClient,
  EmployeeApiClient,
} from "src/Pages/Company/OrganizationStructure/apiClient/OrganizationStructureApiClient";
import {
  IAreasOrgchartData,
  IEmployeesOrgchartData,
  IAreaMember,
  ICommonIQReturnType,
  IAreasSearch,
} from "src/Pages/Company/OrganizationStructure/ConfigureStructure/Tabs/StructureView/StructureView.models";
import { IAreaData } from "src/Pages/Company/OrganizationStructure/ConfigureStructure/Tabs/StructureAreas/StructureAreas.models";
import {
  Employee,
  ICreateArea,
} from "src/Pages/Company/OrganizationStructure/Areas/Area.models";
import {
  IModuleSettings,
  TModuleSettingsPayload,
} from "src/Pages/Company/OrganizationStructure/ModuleSettings/moduleSettings.models";
import { IDeleteArea } from "src/Pages/Company/OrganizationStructure/api/useDeleteArea";
import { DownloadDataConfig } from "src/Pages/Company/OrganizationStructure/ConfigureStructure/Tabs/StructureAreas/DataManagment/DataManagment.models";
import {
  AddPosition,
  AddPositionResponse,
  EditPositionConfig,
  EditPositionResponse,
  PositionsListResponse,
} from "src/Pages/Company/OrganizationStructure/Positions/positions.models";
import {
  AddLocalizationResponse,
  CreateLocalizationConfig,
  EditLocalizationParams,
  EditLocalizationResponse,
  LocalizationListResponse,
} from "src/Pages/Company/OrganizationStructure/Localizations/localizations.models";

import { SyncErrorsResponse } from "src/Pages/Company/EmployeeManagement/SyncErrors/models/syncErrors.models";
import { SyncErrorDetail } from "src/Pages/Company/EmployeeManagement/SyncErrors/Details/syncErrorDetails.models";

export const fetchStructureData = async (
  companyId: string,
  structureId: string,
  {
    signal,
  }: {
    signal: AbortSignal | undefined;
  }
) => {
  const { data } = await OrganizationStructureApiClient.get(
    `/v1/companies/${companyId}/structures/${structureId}`,
    {
      signal,
    }
  );
  return data;
};

export const changeVisibility = async (
  companyId: string,
  structureId: string,
  switchState: IChangeVisibility
) => {
  const { data } = await OrganizationStructureApiClient.put(
    `/v1/companies/${companyId}/structures/${structureId}/visibility`,
    switchState
  );
  return data;
};

export const saveEditStructureForm = async (
  companyId: string,
  structureId: string,
  formData: IFormValues
) => {
  const { data } = await OrganizationStructureApiClient.put(
    `/v1/companies/${companyId}/structures/${structureId}`,
    formData
  );
  return data;
};

export const createStructure = async (
  companyId: string,
  formData: ICreateStructure
) => {
  const { data } = await OrganizationStructureApiClient.post(
    `/v1/companies/${companyId}/structures`,
    formData
  );
  return data;
};

export const fetchListOfStructures = async (
  companyId: string,
  {
    signal,
  }: {
    signal: AbortSignal | undefined;
  }
) => {
  const data = await OrganizationStructureApiClient.get(
    `/v1/companies/${companyId}/structures`,
    {
      signal,
    }
  );
  return data;
};

export const createArea = async (
  companyId: string,
  structureId: string,
  formData: ICreateArea
) => {
  const { data } = await OrganizationStructureApiClient.post(
    `/v1/companies/${companyId}/structures/${structureId}/areas`,
    formData
  );
  return data;
};

export const fetchAreas = async (
  companyId: string,
  structureId: string,
  {
    signal,
  }: {
    signal: AbortSignal | undefined;
  },
  filters: string | null,
  page: number,
  sortType: string
): Promise<IAreaData> => {
  const { data } = await OrganizationStructureApiClient.get(
    `/v1/companies/${companyId}/structures/${structureId}/areas`,
    {
      signal,
      params: {
        filters,
        page,
        sort: `areaName:${sortType}`,
      },
    }
  );
  return data;
};

export const fetchArea = async (
  companyId: string,
  structureId: string,
  areaId: string,
  {
    signal,
    queryParams,
  }: {
    signal: AbortSignal | undefined;
    queryParams?: Record<string, string>;
  }
) => {
  const { data } = await OrganizationStructureApiClient.get(
    `/v1/companies/${companyId}/structures/${structureId}/areas/${areaId}`,
    {
      signal,
      params: queryParams,
    }
  );
  return data;
};

export const changeArea = async (
  companyId: string,
  structureId: string,
  areaId: string,
  formData: ICreateArea
) => {
  const { data } = await OrganizationStructureApiClient.put(
    `v1/companies/${companyId}/structures/${structureId}/areas/${areaId}`,
    formData
  );
  return data;
};

export const fetchEmployeeOrgchart = async (
  companyId: string,
  structureId: string
): Promise<IEmployeesOrgchartData> => {
  const { data } = await OrganizationStructureApiClient.get(
    `v1/companies/${companyId}/structures/${structureId}/employees-org-chart`
  );
  return data;
};

export const fetchAreasOrgchart = async (
  companyId: string,
  structureId: string
): Promise<IAreasOrgchartData> => {
  const { data } = await OrganizationStructureApiClient.get(
    `v1/companies/${companyId}/structures/${structureId}/areas-org-chart`
  );
  return data;
};

export const fetchAreaMembers = async ({
  areaId,
  pageParam,
  companyId,
  structureId,
}: {
  areaId: string;
  pageParam: string | null;
  companyId: string;
  structureId: string;
}): Promise<ICommonIQReturnType<IAreaMember>> => {
  const { data } = await OrganizationStructureApiClient.get(
    `/v1/companies/${companyId}/structures/${structureId}/areas/${areaId}/members`,
    {
      params: {
        cursor: pageParam,
      },
    }
  );
  return data;
};

export const fetchAreaHierarchy = async (
  companyId: string,
  structureId: string,
  areaId: string,
  {
    signal,
  }: {
    signal: AbortSignal | undefined;
  }
) => {
  const { data } = await OrganizationStructureApiClient.get(
    `/v1/companies/${companyId}/structures/${structureId}/areas/${areaId}/hierarchy`,
    {
      signal,
    }
  );
  return data;
};

export interface IEmployeesList {
  content: Employee[];
  totalElements?: number;
  nextCursor: string;
  size: number;
}

interface ICommonInfinityQParams {
  companyId: string;
  structureId: string | undefined;
  pageParam: string | null;
  filters: string | null;
}

export const fetchEmployeesList = async ({
  structureId,
  companyId,
  pageParam,
  filters,
}: ICommonInfinityQParams): Promise<IEmployeesList> => {
  const { data } = await OrganizationStructureApiClient.get(
    `/v1/companies/${companyId}/structures/${structureId}/employees`,
    { params: { cursor: pageParam, filters } }
  );
  return data;
};

export const fetchAreasSearch = async ({
  structureId,
  companyId,
  pageParam,
  filters,
}: ICommonInfinityQParams): Promise<ICommonIQReturnType<IAreasSearch>> => {
  const { data } = await OrganizationStructureApiClient.get(
    `/v1/companies/${companyId}/structures/${structureId}/areas/search`,
    { params: { cursor: pageParam, filters } }
  );
  return data;
};

export const downloadAreasData = async (config: DownloadDataConfig) => {
  const { companyId, structureId, contentType, template } = config;
  // eslint-disable-next-line
  return await OrganizationStructureApiClient.get(
    `v1/companies/${companyId}/structures/${structureId}/areas/export`,
    {
      data: {},
      headers: { "Content-Type": contentType },
      responseType: "blob",
      params: template ? { template: true } : {},
    }
  );
};

export const downloadStructureData = async (config: DownloadDataConfig) => {
  const { companyId, structureId, contentType, template } = config;
  // eslint-disable-next-line
  return await OrganizationStructureApiClient.get(
    `v1/companies/${companyId}/structures/${structureId}/employees/export`,
    {
      data: {},
      headers: { "Content-Type": contentType },
      responseType: "blob",
      params: template ? { template: true } : {},
    }
  );
};

export const downloadLocalizationsData = async ({
  companyId,
  structureId,
  contentType,
  template,
}: {
  companyId: string;
  structureId: string;
  contentType: string;
  template?: boolean;
}) =>
  // eslint-disable-next-line
  await OrganizationStructureApiClient.get(
    `v1/companies/${companyId}/structures/${structureId}/localizations/export`,
    {
      data: {},
      headers: { "Content-Type": contentType, Accept: contentType },
      responseType: "blob",
      params: template ? { template: true } : {},
    }
  );

export const downloadPositionsData = async ({
  companyId,
  structureId,
  contentType,
  template,
}: {
  companyId: string;
  structureId: string;
  contentType: string;
  template?: boolean;
}) =>
  // eslint-disable-next-line
  await OrganizationStructureApiClient.get(
    `v1/companies/${companyId}/structures/${structureId}/positions/export`,
    {
      data: {},
      headers: { "Content-Type": contentType, Accept: contentType },
      responseType: "blob",
      params: template ? { template: true } : {},
    }
  );

export const fetchAreaMembership = async (
  companyId: string,
  employeeId: string | null,
  {
    signal,
  }: {
    signal: AbortSignal | undefined;
  }
) => {
  const { data } = await OrganizationStructureApiClient.get(
    `/v1/companies/${companyId}/employees/${employeeId}/area-membership`,
    {
      signal,
    }
  );
  return data;
};

export const fetchModuleSettings = async (
  companyId: string,
  {
    signal,
  }: {
    signal: AbortSignal | undefined;
  }
): Promise<IModuleSettings> => {
  const { data } = await OrganizationStructureApiClient.get(
    `/v1/companies/${companyId}/modules-configuration`,
    { signal }
  );

  return data;
};

export const changeModuleSettings = async (
  companyId: string,
  payload: TModuleSettingsPayload
) => {
  const { data } = await OrganizationStructureApiClient.put(
    `/v1/companies/${companyId}/modules-configuration`,
    payload
  );

  return data;
};

export const deleteArea = async ({
  companyId,
  structureId,
  areaId,
}: IDeleteArea) => {
  const { data } = await OrganizationStructureApiClient.delete(
    `/v1/companies/${companyId}/structures/${structureId}/areas/${areaId}`
  );
  return data;
};

export const fetchEmployee = async (
  employeeId: string
): Promise<{ email: string | null; firstName: string; lastName: string }> => {
  const { data } = await EmployeeApiClient.get(
    `/v1/rest/employees/${employeeId}`
  );

  return data;
};

export const fetchPositionsList = async ({
  structureId,
  companyId,
  page,
  size,
  sort,
  filters,
}: {
  structureId: string;
  companyId: string;
  page?: number;
  size?: number;
  sort?: string;
  filters?: string;
}): Promise<PositionsListResponse> => {
  const { data } =
    await OrganizationStructureApiClient.get<PositionsListResponse>(
      `/v1/companies/${companyId}/structures/${structureId}/positions-list`,
      {
        params: {
          page: page !== undefined ? page + 1 : 1,
          size,
          ...(sort && { sort }),
          ...(filters && { filters }),
        },
      }
    );
  return data;
};

export const fetchLocalizationsList = async ({
  structureId,
  companyId,
  page,
  size,
  sort,
  filters,
}: {
  structureId: string;
  companyId: string;
  page?: number;
  size?: number;
  sort?: string;
  filters?: string;
}): Promise<LocalizationListResponse> => {
  const { data } =
    await OrganizationStructureApiClient.get<LocalizationListResponse>(
      `/v1/companies/${companyId}/structures/${structureId}/localizations-list`,
      {
        params: {
          page: page !== undefined ? page + 1 : 1,
          size,
          ...(sort && { sort }),
          ...(filters && { filters }),
        },
      }
    );
  return data;
};

export const createPosition = async (
  companyId: string,
  structureId: string,
  formData: AddPosition
): Promise<AddPositionResponse> => {
  const { data } = await OrganizationStructureApiClient.post(
    `/v1/companies/${companyId}/structures/${structureId}/positions`,
    formData
  );
  return data;
};

export const editPosition = async ({
  companyId,
  structureId,
  positionId,
  formData,
}: EditPositionConfig): Promise<EditPositionResponse> => {
  const { data } = await OrganizationStructureApiClient.put(
    `/v1/companies/${companyId}/structures/${structureId}/positions/${positionId}`,
    formData
  );
  return data;
};

export const createLocalization = async ({
  companyId,
  structureId,
  formData,
}: CreateLocalizationConfig): Promise<AddLocalizationResponse> => {
  const { data } = await OrganizationStructureApiClient.post(
    `/v1/companies/${companyId}/structures/${structureId}/localizations`,
    formData
  );
  return data;
};

export const deleteLocalization = async ({
  companyId,
  structureId,
  localizationId,
}: {
  companyId: string;
  structureId: string;
  localizationId: string;
}) => {
  const { data } = await OrganizationStructureApiClient.delete(
    `/v1/companies/${companyId}/structures/${structureId}/localizations/${localizationId}`
  );
  return data;
};

export const editLocalization = async ({
  companyId,
  structureId,
  localizationId,
  formData,
}: EditLocalizationParams): Promise<EditLocalizationResponse> => {
  const { data } = await OrganizationStructureApiClient.put(
    `/v1/companies/${companyId}/structures/${structureId}/localizations/${localizationId}`,
    formData
  );

  return data;
};

export const importEmployeeData = async ({
  companyId,
  file,
}: {
  companyId: string;
  file: File;
}) => {
  const fileBase64 = await new Promise<string>((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result as string);
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });

  const payload = {
    companyId,
    file: fileBase64,
    fileName: file.name,
  };

  const { data } = await EmployeeApiClient.post(
    `/v1/rest/import/ahr-change-employee-attributes`,
    payload,
    {
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
      },
    }
  );

  return data;
};

const importFileToEndpoint = async (url: string, file: File) => {
  const formData = new FormData();
  formData.append("file", file);

  const { data } = await OrganizationStructureApiClient.post(url, formData, {
    headers: { "Content-Type": "multipart/form-data" },
  });

  return data;
};

export const importAreaData = ({
  companyId,
  structureId,
  file,
}: {
  companyId: string;
  structureId: string;
  file: File;
}) =>
  importFileToEndpoint(
    `/v1/companies/${companyId}/structures/${structureId}/areas/import`,
    file
  );

export const importLocalizationData = ({
  companyId,
  structureId,
  file,
}: {
  companyId: string;
  structureId: string;
  file: File;
}) =>
  importFileToEndpoint(
    `/v1/companies/${companyId}/structures/${structureId}/localizations/import`,
    file
  );

export const importPositionData = ({
  companyId,
  structureId,
  file,
}: {
  companyId: string;
  structureId: string;
  file: File;
}) =>
  importFileToEndpoint(
    `/v1/companies/${companyId}/structures/${structureId}/positions/import`,
    file
  );

export interface PotentialParent {
  id: string;
  name: string;
  canBeParent: boolean;
  givenEmployeeInheritsSupervisor: boolean;
  givenEmployeeCanInheritManager: boolean;
}

export interface PotentialParentsResponse {
  content: PotentialParent[];
  size: number;
  nextCursor: string | null;
  totalElements: number;
}

export interface UsePotentialParentsParams {
  companyId: string;
  structureId: string;
  areaId: string;
  withManager?: string;
  query?: string | null;
  size?: number;
  enabled?: boolean;
}

export const fetchPotentialParents = async (
  companyId: string,
  structureId: string,
  areaId: string,
  {
    signal,
    nextCursor,
    query,
    withManager,
    size,
  }: {
    signal?: AbortSignal;
    nextCursor?: string | null;
    query?: string | null;
    withManager?: string;
    size?: number;
  }
): Promise<PotentialParentsResponse> => {
  const { data } =
    await OrganizationStructureApiClient.get<PotentialParentsResponse>(
      `v1/companies/${companyId}/structures/${structureId}/areas/${areaId}/potential-parents`,
      {
        params: {
          ...(nextCursor && { cursor: nextCursor }),
          ...(query && { filters: query }),
          ...(withManager && { withManager }),
          ...(size && { size }),
        },
        signal,
      }
    );
  return data;
};
export interface CanBeAreaMembersManagerRequest {
  members: string[];
  parentArea: string | null;
}

export interface ConflictingSubordinate {
  employeeId: string;
  reason: "NOT_FOUND" | "TREE_PATH_LOOP";
}

export interface CanBeAreaMembersManagerResponse {
  employeeId: string;
  givenEmployeeInheritsSupervisor: boolean | null;
  givenEmployeeCanInheritManager: boolean | null;
  correctSubordinates: string[];
  conflictingSubordinates: ConflictingSubordinate[];
}

export const checkCanBeAreaMembersManager = async (
  companyId: string,
  employeeId: string,
  payload: CanBeAreaMembersManagerRequest
): Promise<CanBeAreaMembersManagerResponse> => {
  const { data } = await OrganizationStructureApiClient.post(
    `/v1/companies/${companyId}/employees/${employeeId}/can-be-area-members-manager`,
    payload
  );
  return data;
};

export const importEmployeeStructureData = ({
  companyId,
  structureId,
  file,
}: {
  companyId: string;
  structureId: string;
  file: File;
}) =>
  importFileToEndpoint(
    `/v1/companies/${companyId}/structures/${structureId}/employees/import`,
    file
  );

export const fetchSyncErrors = async ({
  companyId,
  page,
  size,
  sort,
  filters,
}: {
  companyId: string;
  page?: number;
  size?: number;
  sort?: string;
  filters?: string;
}): Promise<SyncErrorsResponse> => {
  const { data } = await OrganizationStructureApiClient.get<SyncErrorsResponse>(
    `/v1/companies/${companyId}/external-request-errors`,
    {
      params: {
        page: page !== undefined ? page + 1 : 1,
        size,
        ...(sort && { sort }),
        ...(filters && { filters }),
      },
    }
  );
  return data;
};

export const fetchSyncErrorDetails = async ({
  companyId,
  employeeFk,
}: {
  companyId: string;
  employeeFk: string;
}): Promise<SyncErrorDetail[]> => {
  const { data } = await OrganizationStructureApiClient.get<SyncErrorDetail[]>(
    `/v1/companies/${companyId}/external-request-errors-details/${employeeFk}`
  );
  return data;
};

export const deletePosition = async ({
  companyId,
  structureId,
  positionId,
}: {
  companyId: string;
  structureId: string;
  positionId: string;
}) => {
  const { data } = await OrganizationStructureApiClient.delete(
    `/v1/companies/${companyId}/structures/${structureId}/positions/${positionId}`
  );
  return data;
};

export const downloadEmploymentsExport = async ({
  companyId,
  structureId,
  contentType,
  from,
  to,
  filter,
}: {
  companyId: string;
  structureId: string;
  contentType: string;
  from: string;
  to: string;
  filter: string;
}) =>
  // eslint-disable-next-line
  await OrganizationStructureApiClient.get(
    `v1/companies/${companyId}/structures/${structureId}/employees/employments-export`,
    {
      data: {},
      headers: { "Content-Type": contentType, Accept: contentType },
      responseType: "blob",
      params: { from, to, filter },
    }
  );
